import { v4 as uuidv4 } from 'uuid';
import { addErrorToReport } from '@americanexpress/one-app-ducks';
import { getFaasApiUrl } from '@/selectors/configSelectors';
import { getLoungesApiUrl } from '@/selectors/loungesApiSelector';
import { getAuthentication, setAuthentication } from '@/slices/authSlice';
import {
  getWallet,
  setWallet,
  setSelectedProduct,
  setWalletError,
} from '@/slices/walletSlice';
import { getBdaasUrl } from '@/selectors/bdaasSelectors';
import { getUserWallet } from '@/selectors/walletSelector';
import { getIsLoggedIn } from '@/selectors/authSelectors';
import { sortAndIdentifyCardProducts } from '@/helpers/utils';

export const fetchOptions = {
  mode: 'cors',
  credentials: 'include',
};

export const readUserSession = async (faasApiUrl, fetchClient = fetch) => {
  const response = await fetchClient(`${faasApiUrl}/ReadUserSession.v1`, {
    ...fetchOptions,
    'one-data-correlation-id': uuidv4(),
  });
  return response.ok;
};

export const fetchWallet =
  (bdaasUrl, correlationId, fetchClient = fetch) =>
  async (dispatch) => {
    const walletFetchOptions = {
      ...fetchOptions,
      method: 'POST',
      headers: { correlation_id: correlationId },
    };

    await dispatch(getWallet());
    const response = await fetchClient(
      `${bdaasUrl}/user/profile`,
      walletFetchOptions
    );

    if (!response.ok) {
      throw new Error(
        `Failed to fetch wallet: ${response.status} ${response.statusText}`
      );
    }
    return response.json();
  };

export const fetchLoungeGuestAccess =
  (token, correlationId, fetchClient = fetch) =>
  async (dispatch, getState) => {
    const state = getState();
    const loungesApiUrl = getLoungesApiUrl(state);
    const options = {
      ...fetchOptions,
      headers: {
        account_token: token,
        correlation_id: correlationId,
        channel_id: 'WEB',
      },
    };

    const response = await fetchClient(
      `${loungesApiUrl}/member/benefit_active`,
      options
    );

    if (!response.ok) {
      throw new Error(
        `Failed to fetch benefit status: ${response.status} ${response.statusText}`
      );
    }

    return response.json();
  };

export const checkIsLoggedInThunk =
  (fetchClient) => async (dispatch, getState) => {
    const state = getState();
    let isAuthenticated = getIsLoggedIn(state);
    if (isAuthenticated === null) {
      dispatch(getAuthentication());
      const faasApiUrl = getFaasApiUrl(state);
      isAuthenticated = await readUserSession(faasApiUrl, fetchClient);
      dispatch(setAuthentication(isAuthenticated));
    }
    return isAuthenticated;
  };

export const isAuthenticatedAndLoadWallet =
  ({ fetchClient }) =>
  async (dispatch, getState) => {
    const isAuthenticated = await dispatch(checkIsLoggedInThunk(fetchClient));
    const state = getState();
    const bdaasUrl = getBdaasUrl(state);
    const wallet = getUserWallet(state);
    const correlationId = uuidv4();

    if (!isAuthenticated) {
      // If not authenticated, set wallet to empty array and selected product to empty object
      dispatch(setWallet([]));
      dispatch(setSelectedProduct({}));
      return;
    }

    if (wallet === null) {
      try {
        const walletData = await dispatch(
          fetchWallet(bdaasUrl, correlationId, fetchClient)
        );
        let {
          userSession: {
            profile: { accounts = [] },
          },
        } = walletData;
        accounts = sortAndIdentifyCardProducts(accounts);

        // check centurion lounge guest benefit status for eligible accounts
        accounts = await Promise.all(
          accounts.map(async (account) => {
            try {
              if (account.guestBenefitEligible) {
                const loungeGuestAccess = await dispatch(
                  fetchLoungeGuestAccess(
                    account.accountToken,
                    correlationId,
                    fetchClient
                  )
                );
                return {
                  ...account,
                  hasGuestBenefit: loungeGuestAccess.isGuestAccessBenefitActive,
                };
              }

              return {
                ...account,
                hasGuestBenefit: null,
              };
            } catch (error) {
              dispatch(
                addErrorToReport(error, {
                  collectionMethod: 'isAuthenticatedAndLoadWallet',
                  moduleID: 'travel-lounge-finder-root',
                  correlationId,
                  caught: true,
                })
              );
              return {
                ...account,
                hasGuestBenefit: null,
              };
            }
          })
        );

        dispatch(setWallet(accounts));
        dispatch(setSelectedProduct(accounts[0] || {}));
      } catch (e) {
        dispatch(
          addErrorToReport(e, {
            collectionMethod: 'isAuthenticatedAndLoadWallet',
            moduleID: 'travel-lounge-finder-root',
            correlationId,
            caught: true,
          })
        );
        dispatch(setWalletError());
      }
    }
  };
